import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const DEFAULT_SCROLL_THRESHOLD = 100;
const DEFAULT_MAX_SCROLL_SPEED = 25;
const MIN_SCROLL_SPEED = 2;

function findScrollableAncestor(element: HTMLElement): HTMLElement {
    let current = element.parentElement;

    while (current) {
        const { overflowY } = window.getComputedStyle(current);

        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }

        current = current.parentElement;
    }

    return document.documentElement;
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * Math.max(0, Math.min(1, t));
}

export interface DragScrollOptions {
    pluginKey?: PluginKey | string;
    scrollThreshold?: number;
    maxScrollSpeed?: number;
}

const dragScrollPluginDefaultKey = new PluginKey('dragScroll');

const DragScrollPlugin = (options: DragScrollOptions) => {
    const {
        pluginKey = dragScrollPluginDefaultKey,
        scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
        maxScrollSpeed = DEFAULT_MAX_SCROLL_SPEED,
    } = options;

    let scrollContainer: HTMLElement | null = null;
    let rafId: number | null = null;
    let isDragging = false;
    let lastMouseY = 0;

    function scrollLoop() {
        if (!isDragging || !scrollContainer) {
            rafId = null;
            return;
        }

        const rect = scrollContainer.getBoundingClientRect();
        const topThreshold = rect.top + scrollThreshold;
        const bottomThreshold = rect.bottom - scrollThreshold;

        if (lastMouseY < topThreshold) {
            const distance = topThreshold - lastMouseY;
            const speed = lerp(MIN_SCROLL_SPEED, maxScrollSpeed, distance / scrollThreshold);
            scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop - speed);
        } else if (lastMouseY > bottomThreshold) {
            const distance = lastMouseY - bottomThreshold;
            const speed = lerp(MIN_SCROLL_SPEED, maxScrollSpeed, distance / scrollThreshold);
            scrollContainer.scrollTop += speed;
        }

        rafId = requestAnimationFrame(scrollLoop);
    }

    function stopScrollLoop() {
        isDragging = false;
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    function onDragOver(e: DragEvent) {
        lastMouseY = e.clientY;

        if (!isDragging) {
            isDragging = true;
            rafId = requestAnimationFrame(scrollLoop);
        }
    }

    function onDragEnd() {
        stopScrollLoop();
    }

    return {
        unbind() {
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('dragend', onDragEnd);
            document.removeEventListener('drop', onDragEnd);
            stopScrollLoop();
        },
        plugin: new Plugin({
            key: typeof pluginKey === 'string' ? new PluginKey(pluginKey) : pluginKey,

            view(view) {
                scrollContainer = findScrollableAncestor(view.dom as HTMLElement);

                document.addEventListener('dragover', onDragOver);
                document.addEventListener('dragend', onDragEnd);
                document.addEventListener('drop', onDragEnd);

                return {
                    destroy() {
                        document.removeEventListener('dragover', onDragOver);
                        document.removeEventListener('dragend', onDragEnd);
                        document.removeEventListener('drop', onDragEnd);
                        stopScrollLoop();
                    },
                };
            },
        }),
    };
};

export const DragScroll = Extension.create<DragScrollOptions>({
    name: 'dragScroll',

    addOptions() {
        return {
            pluginKey: dragScrollPluginDefaultKey,
            scrollThreshold: DEFAULT_SCROLL_THRESHOLD,
            maxScrollSpeed: DEFAULT_MAX_SCROLL_SPEED,
        };
    },

    addProseMirrorPlugins() {
        return [
            DragScrollPlugin({
                pluginKey: this.options.pluginKey,
                scrollThreshold: this.options.scrollThreshold,
                maxScrollSpeed: this.options.maxScrollSpeed,
            }).plugin,
        ];
    },
});
